import React from "react";
import {
  Body,
  Caption,
  Cell,
  EmptyCell,
  FullRow,
  GroupHead,
  Head,
  HeadCell,
  NoteRow,
  Row,
  Table,
  Wrap,
} from "./GroupedTable.styles";
import type { GroupedTableProps } from "./GroupedTable.types";

/** A table of rows in groups, each under a header row; on a phone each row becomes a card. */
export const GroupedTable = <Row,>({
  columns,
  groups,
  rowKey,
  rowTone,
  rowNote,
  rowFull,
  headAppearance = "default",
  density = "default",
  selectedTone = true,
  caption,
  empty,
  className,
}: GroupedTableProps<Row>): React.JSX.Element => {
  const span = columns.length;
  const toneOf = (row: Row) => {
    const tone = rowTone?.(row) ?? "default";
    return tone === "selected" && !selectedTone ? "default" : tone;
  };
  const compact = density === "compact";
  const filled = groups.filter((group) => group.rows.length > 0);
  return (
    <Wrap className={className}>
      <Table>
        <Caption>{caption}</Caption>
        <Head>
          <tr>
            {columns.map((column) => (
              <HeadCell key={column.key} scope="col" $minWidth={column.minWidth} $fit={column.fit} $plain={headAppearance === "plain"}>
                {column.header}
              </HeadCell>
            ))}
          </tr>
        </Head>
        {filled.length === 0 ? (
          <Body>
            <FullRow $tone="default">
              <EmptyCell colSpan={span}>{empty}</EmptyCell>
            </FullRow>
          </Body>
        ) : (
          filled.map((group) => (
            <Body key={group.id} data-group={group.id}>
              {group.head ? (
                <tr>
                  <GroupHead scope="rowgroup" colSpan={span}>
                    {group.head}
                  </GroupHead>
                </tr>
              ) : null}
              {group.rows.map((row) => {
                const key = rowKey(row);
                const tone = toneOf(row);
                const full = rowFull?.(row) ?? null;
                if (full) {
                  return (
                    <FullRow key={key} $tone={tone} data-tone={tone}>
                      <Cell colSpan={span} $compact={compact}>{full}</Cell>
                    </FullRow>
                  );
                }
                const note = rowNote?.(row) ?? null;
                return (
                  <React.Fragment key={key}>
                    <Row $tone={tone} $hasNote={note !== null} data-tone={tone}>
                      {columns.map((column) => (
                        <Cell key={column.key} $fit={column.fit} $compact={compact} data-label={column.header} data-phone={column.phone ?? "stack"}>
                          {column.render(row)}
                        </Cell>
                      ))}
                    </Row>
                    {note ? (
                      <NoteRow $tone={tone} data-note-for={key}>
                        <Cell colSpan={span} $compact={compact}>{note}</Cell>
                      </NoteRow>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </Body>
          ))
        )}
      </Table>
    </Wrap>
  );
};

export default GroupedTable;
